import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X, Upload, ChevronUp, ChevronDown, GripVertical } from "lucide-react";

const FIELDS = {
  packages: [
    ["name_ar", "text"], ["name_en", "text"], ["category", "select:religious,leisure,domestic,international"], ["destination", "select:iraq,turkey,lebanon,tunisia,oman,uae,egypt,iran,malaysia,thailand,georgia,azerbaijan"],
    ["price", "number"], ["duration_ar", "text"], ["duration_en", "text"], ["rating", "number"], ["image", "text"],
    ["desc_ar", "textarea"], ["desc_en", "textarea"], ["itinerary_ar", "list"], ["itinerary_en", "list"], ["departures", "departures"],
  ],
  offers: [
    ["title_ar", "text"], ["title_en", "text"], ["ref_package", "text"], ["price", "number"], ["old_price", "number"],
    ["badge_ar", "text"], ["badge_en", "text"], ["image", "text"], ["desc_ar", "textarea"], ["desc_en", "textarea"],
  ],
  hotels: [
    ["name_ar", "text"], ["name_en", "text"], ["city", "select:baghdad,erbil,karbala,najaf,basra"], ["city_ar", "text"], ["city_en", "text"],
    ["stars", "number"], ["price", "number"], ["rating", "number"], ["image", "text"], ["amenities_ar", "list"], ["amenities_en", "list"],
  ],
  articles: [
    ["title_ar", "text"], ["title_en", "text"], ["category", "select:tips,destinations"], ["image", "text"],
    ["excerpt_ar", "textarea"], ["excerpt_en", "textarea"], ["body_ar", "lines"], ["body_en", "lines"],
  ],
  group_destinations: [["name_ar", "text"], ["name_en", "text"], ["image", "text"]],
};

const toForm = (item, kind) => Object.fromEntries(FIELDS[kind].map(([k, t]) => [k,
  t === "list" ? (item?.[k] || []).join(", ")
  : t === "lines" ? (item?.[k] || []).join("\n")
  : t === "departures" ? (item?.[k] || []).map((d) => `${d.date} | ${d.price} | ${d.seats ?? 20}`).join("\n")
  : item?.[k] ?? ""]));
const parseDepartures = (txt) => txt.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[0] && p[1])
  .map(([date, price, seats]) => ({ date, price: Number(price), seats: Number(seats) || 20 }));
const fromForm = (form, kind) => Object.fromEntries(FIELDS[kind].map(([k, t]) => [k,
  t === "list" ? form[k].split(",").map((s) => s.trim()).filter(Boolean)
  : t === "lines" ? form[k].split("\n").map((s) => s.trim()).filter(Boolean)
  : t === "departures" ? parseDepartures(form[k] || "")
  : t === "number" ? (form[k] === "" ? null : Number(form[k])) : form[k]]));

export const CatalogManager = () => {
  const { t, lang, money } = useApp();
  const a = t.admin;
  const [kind, setKind] = useState("packages");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(a.uploadTooBig); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success(a.uploaded);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const load = () => { setItems(null); api.get(`/admin/catalog/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([])); };
  useEffect(load, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (item) => { setEditing(item || { _new: true }); setForm(toForm(item, kind)); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = fromForm(form, kind);
      if (editing._new) await api.post(`/admin/catalog/${kind}`, body);
      else await api.put(`/admin/catalog/${kind}/${editing.id}`, body);
      toast.success(a.updated); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const toggle = async (item) => {
    try { await api.put(`/admin/catalog/${kind}/${item.id}`, { active: item.active === false }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (item) => {
    if (!window.confirm(a.confirmDelete)) return;
    try { await api.delete(`/admin/catalog/${kind}/${item.id}`); toast.success(a.deleted); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const label = (it) => it[`name_${lang}`] || it[`title_${lang}`] || it.id;
  const [dragId, setDragId] = useState(null);
  const reorder = async (ids) => {
    setItems(items.slice().sort((x, y) => ids.indexOf(x.id) - ids.indexOf(y.id)));
    try { await api.put(`/admin/catalog/${kind}/reorder`, { ids }); } catch (err) { toast.error(apiError(err.response?.data?.detail)); load(); }
  };
  const move = (idx, dir) => {
    const ids = items.map((x) => x.id); const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]]; reorder(ids);
  };
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = items.map((x) => x.id); const from = ids.indexOf(dragId); const to = ids.indexOf(targetId);
    ids.splice(from, 1); ids.splice(to, 0, dragId); reorder(ids); setDragId(null);
  };
  const tp = (c) => t.tips?.cats?.[c] || c;
  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 focus:border-tigris outline-none text-sm";

  return (
    <div data-testid="catalog-manager">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {Object.keys(FIELDS).map((k) => (
          <button key={k} data-testid={`catalog-kind-${k}`} onClick={() => setKind(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${kind === k ? "bg-gold text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{a.catalog[k]}</button>
        ))}
        <button data-testid="catalog-add-btn" onClick={() => open(null)} className="ms-auto btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"><Plus size={14} /> {a.add}</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        {items === null ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-tigris" /></div> : (
          <table className="w-full text-sm" data-testid={`catalog-table-${kind}`}>
            <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
              {["", a.name, "ID", a.price, a.status, ""].map((h, i) => <th key={i} className="px-4 py-3 text-start font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it, idx) => (
                <tr key={it.id} data-testid={`catalog-row-${it.id}`} draggable onDragStart={() => setDragId(it.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(it.id)}
                  className={`${it.active === false ? "opacity-50" : ""} ${dragId === it.id ? "bg-gold/10" : ""}`}>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-0.5 text-slate-400">
                      <GripVertical size={16} className="cursor-grab" data-testid={`catalog-drag-${it.id}`} />
                      <div className="flex flex-col">
                        <button type="button" data-testid={`catalog-up-${it.id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="hover:text-tigris disabled:opacity-20"><ChevronUp size={14} /></button>
                        <button type="button" data-testid={`catalog-down-${it.id}`} disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="hover:text-tigris disabled:opacity-20"><ChevronDown size={14} /></button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold flex items-center gap-3">
                    {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover" />}{label(it)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{it.id}</td>
                  <td className="px-4 py-3 font-bold text-tigris">{it.price != null ? money(it.price) : (kind === "articles" ? tp(it.category) : "—")}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${it.active === false ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-700"}`}>{it.active === false ? a.hidden : a.visible}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`catalog-toggle-${it.id}`} onClick={() => toggle(it)} title={it.active === false ? a.show : a.hide} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">{it.active === false ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                      <button data-testid={`catalog-edit-${it.id}`} onClick={() => open(it)} className="p-2 rounded-lg hover:bg-tigris/10 text-tigris"><Pencil size={16} /></button>
                      <button data-testid={`catalog-delete-${it.id}`} onClick={() => remove(it)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="catalog-form" className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-800">{editing._new ? a.add : a.edit} — {a.catalog[kind]}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1 rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS[kind].map(([k, type]) => (
                <div key={k} className={["textarea", "list", "departures", "lines"].includes(type) ? "sm:col-span-2" : ""}>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">{a.fields[k] || k}</label>
                  {type.startsWith("select:") ? (
                    <select data-testid={`cf-${k}`} className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required>
                      <option value="">—</option>
                      {type.slice(7).split(",").map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : k === "image" ? (
                    <div className="flex items-center gap-2">
                      {form.image && <img src={form.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" data-testid="cf-image-preview" />}
                      <input data-testid="cf-image" className={inputCls} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
                      <label data-testid="cf-image-upload" className="btn-primary px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap flex items-center gap-1">
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {a.upload}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} data-testid="cf-image-file" />
                      </label>
                    </div>
                  ) : ["textarea", "list", "departures", "lines"].includes(type) ? (
                    <textarea data-testid={`cf-${k}`} rows={type === "departures" || type === "lines" ? 4 : 2} className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={type === "list" ? a.listHint : type === "departures" ? a.departuresHint : type === "lines" ? a.linesHint : ""} />
                  ) : (
                    <input data-testid={`cf-${k}`} type={type} step="any" className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={["name_ar", "name_en", "title_ar", "title_en", "price"].includes(k)} />
                  )}
                </div>
              ))}
            </div>
            <button type="submit" disabled={saving} data-testid="catalog-save-btn" className="btn-gold w-full py-3 rounded-xl font-bold flex justify-center">{saving ? <Loader2 className="animate-spin" size={18} /> : a.save}</button>
          </form>
        </div>
      )}
    </div>
  );
};
